import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import HeaderComponent from "@/components/common/Header/Header";
import BackIcon from "@/styles/all-images/icons/back-button.svg";
import { useNavigate } from "react-router-dom";

interface Permission {
  name: string;
  actions: string[];
}

interface FormValues {
  roleName: string;
  permissions: {
    [key: string]: string[];
    clientProfiles: string[];
    documents: string[];
    contacts: string[];
  };
}

const EditAddRole = () => {
  const navigate = useNavigate();
  const formik = useFormik<FormValues>({
    initialValues: {
      roleName: "",
      permissions: {
        clientProfiles: [],
        documents: [],
        contacts: [],
      },
    },
    validationSchema: Yup.object({
      roleName: Yup.string().required("Role name is required"),
      permissions: Yup.object({
        clientProfiles: Yup.array(),
        documents: Yup.array(),
        contacts: Yup.array(),
      }),
    }),
    onSubmit: (values) => {
      console.log(values);
    },
  });

  const permissions: { [key: string]: Permission } = {
    clientProfiles: {
      name: "Client Profiles",
      actions: ["Add Client", "View Client", "Approve or Reject Action"],
    },
    documents: {
      name: "Documents",
      actions: ["View", "Approve or Reject Action", "Chat"],
    },
    contacts: {
      name: "Contacts",
      actions: [
        "View Tier 1 Contacts",
        "View Tier 2 Contacts",
        "View Tier 3 Contacts",
        "View Tier 4 Contacts",
        "View Tier 5 Contacts",
        "View Tier 6 Contacts",
        "View Tier 7 Contacts",
      ],
    },
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <HeaderComponent />

      {/* Sub Header */}
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <a href="/roles-and-permissions">
                <img src={BackIcon} alt="Back Icon" />
              </a>
              <h1 className="tw-text-xl tw-ml-4 tw-font-semibold font-primary-color">
                Add Role
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="tw-px-8 tw-py-4 tw-mt-[140px]">
        <div className="tw-bg-white custom-card">
          <div>
            <div className="custom-card-header">
              <div className="tw-py-3">
                <div className="form-group tw-mb-6 sm:tw-w-[380px] tw-w-full">
                  <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                    Role Name
                  </p>
                  <input
                    type="text"
                    name="roleName"
                    placeholder="Enter Role Name"
                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md"
                    value={formik.values.roleName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.roleName && formik.errors.roleName && (
                    <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                      {formik.errors.roleName}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="custom-card-body">
              {/* Permissions */}
              <div className="tw-mt-6">
                <h3 className="font-size-base tw-font-semibold font-primary-color tw-mb-4">
                  Select Permissions
                </h3>
                {Object.entries(permissions).map(([key, section]) => (
                  <div key={key} className="tw-mb-6">
                    <div className="inline-custom-card">
                      <div className="inline-custom-card-header">
                        <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                          <h4 className="font-gray-color-dark font-weight-medium">
                            {section.name}
                          </h4>
                          <label className="tw-flex tw-items-center tw-space-x-3">
                            <input
                              type="checkbox"
                              onChange={(e) => {
                                const newActions = e.target.checked
                                  ? section.actions
                                  : [];
                                formik.setFieldValue(
                                  `permissions.${key}`,
                                  newActions,
                                );
                              }}
                              checked={
                                formik.values.permissions[key].length ===
                                section.actions.length
                              }
                              className="tw-w-4 tw-h-4 tw-rounded tw-border-gray-300"
                            />
                            <span className="tw-text-sm font-gray-color-dark">
                              Select All
                            </span>
                          </label>
                        </div>
                      </div>
                      <div className="inline-custom-card-body">
                        <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-4 md:tw-gap-2 tw-gap-1">
                          {section.actions.map((action) => (
                            <label
                              key={action}
                              className="tw-flex tw-items-center tw-space-x-3"
                            >
                              <input
                                type="checkbox"
                                checked={formik.values.permissions[
                                  key
                                ].includes(action)}
                                onChange={(e) => {
                                  const currentActions =
                                    formik.values.permissions[key];
                                  const newActions = e.target.checked
                                    ? [...currentActions, action]
                                    : currentActions.filter(
                                        (a) => a !== action,
                                      );
                                  formik.setFieldValue(
                                    `permissions.${key}`,
                                    newActions,
                                  );
                                }}
                                className="tw-w-4 tw-h-4 tw-rounded tw-border-gray-300"
                              />
                              <span className="tw-text-sm font-gray-color-dark">
                                {action}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="tw-flex tw-justify-center tw-items-center tw-mt-6">
          <button
            type="button"
            className="tw-mr-4 tw-px-4 tw-py-2 tw-border tw-border-[#E9EAEB] tw-rounded-md"
            onClick={() => navigate("/roles-and-permissions")}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
          >
            Save
          </button>
        </div>
      </div>
    </form>
  );
};

export default EditAddRole;
